import { RAD } from './constants.js';
import { inspectSurface } from './tasks.js';
import { cameraViews, inCameraFrame } from './optics.js';
import { blocked } from './geometry.js';
import { sceneWalls } from './siteGeometry.js';
import { personDimensions, personParts, targetWorldPoint } from './sceneObjects.js';
import { vehiclePlates } from './vehiclePlates.js';
import { inspectPlate } from './plateInspection.js';
import { withBuildingVisibility } from './buildingVisibility.js';
import { formatDensity, formatLength } from './units.js';

export function targetPoint(S, target) {
  if (target.kind === 'vehicle') {
    const plate = vehiclePlates(S, target, 'target')[0];
    if (plate) return { x: plate.x, y: plate.y, height: plate.heightAboveGround };
  }
  const d = personDimensions(S, target);
  return targetWorldPoint(target, target.kind === 'vehicle' ? 2.23 : d.faceOffset, 0, target.kind === 'vehicle' ? S.opt.hPlate : d.faceHeight);
}
export function inspectTarget(S, target, cameraId, moduleId) {
  const camera = S.cams.find(c => c.id === cameraId);
  if (!camera) return null;
  S = withBuildingVisibility(S);
  if (target.kind === 'vehicle') {
    const modules = moduleId === undefined ? cameraViews(S, camera).map(v => v.module?.id) : [moduleId];
    return vehiclePlates(S, target, 'target').flatMap(p => modules.map(id => inspectPlate(S, p, cameraId, id))).filter(Boolean)
      .sort((a, b) => Number(b.met) - Number(a.met) || b.visible - a.visible || a.yaw - b.yaw)[0] || { met: false, reason: 'No plate fitted' };
  }
  const p = targetPoint(S, target);
  const views = cameraViews(S, camera), d = personDimensions(S, target), person = target.kind !== 'vehicle';
  const check = view => ({ cam: camera, ...inspectSurface(S, view.cam, p, { _single: true, height: p.height, req: person ? (S.opt.profile === 'iec-2025' ? 'validate' : 'identify') : 'plate', heading: target.pan,
    ...(person ? { width: d.faceWidth, targetHeight: d.faceSize } : {}) }, view.g), moduleId: view.module?.id });
  const candidates = moduleId !== undefined ? [views.find(v => v.module?.id === moduleId) || views[0]].filter(Boolean) : views;
  const chosen = candidates.map(view => ({ view, result: check(view) })).sort(({ result: x }, { result: y }) => Number(y.met) - Number(x.met) || y.visible - x.visible || y.density - x.density)[0];
  if (!chosen) return { met: false, reason: 'Missing model specifications' };
  const { view, result } = chosen;
  if (person) {
    const walls = sceneWalls(S);
    const probes = personParts(S, target).filter(p => ['head', 'torso', 'hips', 'leg', 'shin', 'shoe'].includes(p.role)).map(part => {
      const p = targetWorldPoint(target, part.x, part.y, part.z), framed = inCameraFrame(view.cam, view.g, p.x, p.y, p.height);
      return { ...p, part: part.role, framed, clear: framed && !blocked(view.cam.x, view.cam.y, view.cam.h, p.x, p.y, p.height, walls) };
    });
    Object.assign(result, { faceHeight: d.faceHeight, facePixels: result.density * d.faceWidth, bodyProbes: probes, bodyVisible: probes.filter(p => p.clear).length, bodyTotal: probes.length });
  }
  if (result?.met && (result.cam.x - p.x) * Math.cos(target.pan * RAD) + (result.cam.y - p.y) * Math.sin(target.pan * RAD) <= 0) return { ...result, met: false, reason: 'Target faces away from this camera' };
  return result;
}

export function targetReadout(result, doc) {
  if (!result) return 'No camera selected';
  const detail = Number.isFinite(result.density) ? ` · ${formatDensity(result.density, doc)}` : '';
  const person = result.bodyProbes ? ` · face at ${formatLength(result.faceHeight, doc, 2)} · ≈${Math.round(result.facePixels)} px across face · ${result.bodyVisible}/${result.bodyTotal} body points visible` : '';
  const plate = result.plate ? ` · ${result.plate.side} plate · ${Math.round(result.pixelWidth)} px across · ${result.visible}/${result.total} probes visible` : '';
  return result.reason + detail + person + plate;
}
